import type { BridgeImportResult, Consent, CustomerDetail, DocumentRef, Note, Policy, ServiceRequest, Task } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, FileText, IdCard, RefreshCw, Shield, ShieldOff, FilePen, Pencil, Plus, Sparkles, Phone, Mail, MapPin, Upload, FileInput } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { CarrierPanel } from "../components/CarrierPanel";
import { Conversation } from "../components/Conversation";
import { CustomerEditSheet } from "../components/CustomerEditSheet";
import { FormDraftsPanel } from "../components/FormDraftsPanel";
import { PolicySheet } from "../components/PolicySheet";
import { ModeBadge } from "../components/ModeBadge";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, ErrorState, Input, LoadingState, Select, Tabs, Textarea } from "../components/ui";
import { translate, useLocale, useT } from "../i18n";
import { fileToBase64, formatBytes, formatPhone, money, relativeDays, shortDate } from "../theme";
import { StartQuoteSheet } from "./StartQuote";
import { AddNoticeSheet, NoticeList } from "./Notices";
import { CustomerClaims } from "./Claims";
import { CustomerPayments } from "./Payments";
import { CustomerSelfService } from "./SelfService";
import { HouseholdCard } from "./Household";
import type { NoticeRow, PolicyChange } from "@agency/core";
import { daysBetween } from "./Today";

type Tab = "overview" | "messages" | "quotes" | "policies" | "forms" | "carrier" | "documents" | "consents" | "activity";

export function CustomerScreen() {
  const { id = "" } = useParams();
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["customer", id];
  const q = useQuery({ queryKey: key, queryFn: async () => (await callAction<"agency.customer.get", CustomerDetail>("agency.customer.get", { customerId: id })).result, enabled: !!id });
  const [tab, setTab] = useState<Tab>("overview");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | "cancel" | "reinstate">(null);
  const [policyId, setPolicyId] = useState<string>("");
  const [editOpen, setEditOpen] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["today"] });
    qc.invalidateQueries({ queryKey: ["renewals"] });
    qc.invalidateQueries({ queryKey: ["pipeline"] });
  };

  const policyAction = useMutation({
    mutationFn: (v: { policyId: string; action: "renew" | "endorse" | "cancel" | "reinstate" | "remarket_toggle" }) => callAction<"agency.policy.action", Policy>("agency.policy.action", v),
    onSuccess: (r, v) => {
      invalidate();
      toast({ title: t("policy.actionLogged"), body: `${t(`policy.${v.action === "remarket_toggle" ? "renew" : v.action}`)} · ${r.result.carrier} ${r.result.policyNumber}`, tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const serviceRequest = useMutation({
    mutationFn: (v: { policyId: string; kind: "id_card" | "certificate" }) => callAction<"agency.service_request.create", ServiceRequest>("agency.service_request.create", v),
    onSuccess: () => {
      invalidate();
      toast({ title: t("policy.requestCreated"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const c = d.customer;
  const activePolicies = d.policies.filter((p) => p.status === "active" || p.status === "pending_cancel" || p.status === "cancelled");
  const selectedPolicy = d.policies.find((p) => p.id === policyId) ?? activePolicies[0] ?? d.policies[0];
  const nextRenewal = d.policies.filter((p) => p.status === "active").map((p) => p.expirationDate).sort()[0];
  const nextTask = d.tasks.filter((x) => x.status === "open").sort((a, b) => a.dueAt.localeCompare(b.dueAt))[0];
  const latestApp = [...d.applications].sort((a, b) => b.version - a.version)[0];
  const ownerName = config.users.find((u) => u.id === c.ownerUserId)?.name;

  const act = (action: "renew" | "endorse" | "cancel" | "reinstate") => {
    if (!selectedPolicy) return;
    if (action === "cancel" || action === "reinstate") return setConfirm(action);
    policyAction.mutate({ policyId: selectedPolicy.id, action });
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold">{c.kind === "business" && c.businessName ? c.businessName : `${c.firstName} ${c.lastName}`}</h1>
              {c.kind === "business" ? <Badge><Building2 className="h-3 w-3" /> {t("customers.kind.business")}{c.businessName ? ` · ${c.firstName} ${c.lastName}` : ""}</Badge> : null}
              <Badge tone={c.status === "active" ? "green" : c.status === "prospect" ? "blue" : "neutral"}>{t(`customers.status.${c.status}`)}</Badge>
              {c.tags.includes("imported") ? <Badge tone="blue">{t("customers.imported")}</Badge> : null}
              {c.sample ? <Badge>{t("customers.sample")}</Badge> : null}
              <Badge tone={c.preferredLocale === "es" ? "amber" : "neutral"}>{c.preferredLocale === "es" ? t("common.spanish") : t("common.english")}</Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink/70">
              {c.phones[0] ? <a className="inline-flex items-center gap-1 min-h-[32px] hover:underline" href={`tel:${c.phones[0].number}`}><Phone className="h-4 w-4" />{formatPhone(c.phones[0].number)}</a> : null}
              {c.email ? <a className="inline-flex items-center gap-1 min-h-[32px] hover:underline" href={`mailto:${c.email}`}><Mail className="h-4 w-4" />{c.email}</a> : null}
              {c.address ? <span className="inline-flex items-center gap-1 min-h-[32px]"><MapPin className="h-4 w-4" />{c.address.city}, {c.address.state}</span> : null}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <span><span className="text-ink/55">{t("common.nextRenewal")}:</span> <strong>{nextRenewal ? `${shortDate(nextRenewal, locale)} (${relativeDays(daysBetween(nextRenewal), locale)})` : "—"}</strong></span>
              <span><span className="text-ink/55">{t("customer.nextTask")}:</span> <strong>{nextTask ? `${nextTask.title} · ${relativeDays(daysBetween(nextTask.dueAt), locale)}` : t("customer.noTask")}</strong></span>
              {ownerName ? <span><span className="text-ink/55">{t("common.owner")}:</span> <strong>{ownerName}</strong></span> : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 md:flex-col md:items-stretch">
            <Button size="lg" onClick={() => setQuoteOpen(true)}>
              <Sparkles className="h-5 w-5" /> {t("customer.startQuote")}
            </Button>
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" /> {t("customers.edit")}
            </Button>
          </div>
        </div>

        {d.policies.length > 0 ? (
          <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center">
            {d.policies.length > 1 ? (
              <Select aria-label={t("policy.pickPolicy")} value={selectedPolicy?.id ?? ""} onChange={(e) => setPolicyId(e.target.value)} className="md:w-72">
                {d.policies.map((p) => (
                  <option key={p.id} value={p.id}>{p.carrier} · {p.line} · {p.policyNumber}</option>
                ))}
              </Select>
            ) : (
              <span className="text-sm text-ink/70 md:w-72 truncate">{selectedPolicy?.carrier} · {selectedPolicy?.line} · {selectedPolicy?.policyNumber}</span>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => act("renew")}><RefreshCw className="h-4 w-4" />{t("policy.renew")}</Button>
              <Button variant="outline" size="sm" onClick={() => act("endorse")}><FilePen className="h-4 w-4" />{t("policy.endorse")}</Button>
              <Button variant="outline" size="sm" onClick={() => act("cancel")}><ShieldOff className="h-4 w-4" />{t("policy.cancel")}</Button>
              <Button variant="outline" size="sm" onClick={() => act("reinstate")}><Shield className="h-4 w-4" />{t("policy.reinstate")}</Button>
              <Button variant="outline" size="sm" onClick={() => selectedPolicy && serviceRequest.mutate({ policyId: selectedPolicy.id, kind: "id_card" })}><IdCard className="h-4 w-4" />{t("policy.idCard")}</Button>
              <Button variant="outline" size="sm" onClick={() => selectedPolicy && serviceRequest.mutate({ policyId: selectedPolicy.id, kind: "certificate" })}><FileText className="h-4 w-4" />{t("policy.coi")}</Button>
            </div>
          </div>
        ) : null}
      </Card>

      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "overview", label: t("customer.tabs.overview") },
          { id: "messages", label: t("nav.inbox") },
          { id: "quotes", label: t("customer.tabs.quotes"), count: d.quotes.length },
          { id: "policies", label: t("customer.tabs.policies"), count: d.policies.length },
          { id: "forms", label: t("nav.forms"), count: d.formDrafts.length },
          { id: "carrier", label: t("nav.carriers"), count: d.carrierRequests.length },
          { id: "documents", label: t("customer.tabs.documents"), count: d.documents.length },
          { id: "consents", label: t("consents.title"), count: d.consents.length },
          { id: "activity", label: t("customer.tabs.activity"), count: d.timeline.length },
        ]}
      />

      {tab === "overview" ? <OverviewTab d={d} onChanged={invalidate} /> : null}
      {tab === "messages" ? (
        <Card className="p-4">
          <Conversation customerId={c.id} compact />
        </Card>
      ) : null}
      {tab === "quotes" ? <QuotesTab d={d} onChanged={invalidate} /> : null}
      {tab === "policies" ? <PoliciesTab d={d} /> : null}
      {tab === "forms" ? <FormDraftsPanel customerId={c.id} /> : null}
      {tab === "carrier" ? <CarrierPanel customerId={c.id} /> : null}
      {tab === "documents" ? <DocumentsTab d={d} latestAppRef={latestApp ? `application:${latestApp.id}@v${latestApp.version}` : null} onChanged={invalidate} /> : null}
      {tab === "consents" ? <ConsentsTab d={d} onChanged={invalidate} /> : null}
      {tab === "activity" ? <ActivityTab d={d} /> : null}

      <StartQuoteSheet open={quoteOpen} onClose={() => setQuoteOpen(false)} customerId={c.id} dob={c.dob} />
      <CustomerEditSheet open={editOpen} onClose={() => setEditOpen(false)} customer={c} onSaved={invalidate} />
      <ConfirmDialog
        open={confirm === "cancel"}
        title={t("policy.cancel.title")}
        body={t("policy.cancel.body")}
        confirmLabel={t("policy.cancel.confirm")}
        keepLabel={t("policy.cancel.keep")}
        danger
        onKeep={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          if (selectedPolicy) policyAction.mutate({ policyId: selectedPolicy.id, action: "cancel" });
        }}
      />
      <ConfirmDialog
        open={confirm === "reinstate"}
        title={t("policy.reinstate.title")}
        body={t("policy.reinstate.body")}
        confirmLabel={t("policy.reinstate.confirm")}
        keepLabel={t("policy.reinstate.keep")}
        onKeep={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          if (selectedPolicy) policyAction.mutate({ policyId: selectedPolicy.id, action: "reinstate" });
        }}
      />
    </div>
  );
}

function OverviewTab({ d, onChanged }: { d: CustomerDetail; onChanged: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState(new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));
  const addNote = useMutation({
    mutationFn: () => callAction<"agency.note.add", Note>("agency.note.add", { customerId: d.customer.id, body: note, locale }),
    onSuccess: () => {
      setNote("");
      onChanged();
      toast({ title: t("customer.noteAdded"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const addTask = useMutation({
    mutationFn: () => callAction<"agency.task.create", Task>("agency.task.create", { customerId: d.customer.id, title: taskTitle, dueAt: new Date(`${taskDue}T17:00:00`).toISOString(), kind: "follow_up" }),
    onSuccess: () => {
      setTaskTitle("");
      onChanged();
      toast({ title: t("task.created"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const c = d.customer;
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="md:col-span-2 space-y-4">
        <Card>
          <CardHeader title={t("customer.brief")} action={<Badge tone="blue"><Sparkles className="h-3 w-3" /> {t("customer.brief.badge")}</Badge>} />
          <div className="px-5 pb-5 text-ink/80">
            {d.accountBrief ? (
              <>
                <p>{d.accountBrief.text}</p>
                <p className="mt-2 text-xs text-ink/50">{d.accountBrief.generatedBy} · {d.accountBrief.locale.toUpperCase()}</p>
              </>
            ) : (
              <p className="text-ink/55">{t("customer.brief.none")}</p>
            )}
          </div>
        </Card>
        <CustomerNotices customerId={c.id} />
        <CustomerClaims customerId={c.id} policies={d.policies} />
        <CustomerPayments customerId={c.id} policies={d.policies} />
        <CustomerSelfService customerId={c.id} policies={d.policies} documents={d.documents} />
        <HouseholdCard customer={c} />
        <Card>
          <CardHeader title={t("customer.notes")} />
          <form
            className="px-5 pb-3 flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (note.trim()) addNote.mutate();
            }}
          >
            <Textarea className="flex-1 min-h-[64px]" placeholder={t("customer.notePlaceholder")} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button type="submit" loading={addNote.isPending}>{t("customer.addNote")}</Button>
          </form>
          <ul className="divide-y divide-ink/5">
            {d.notes.length === 0 ? <li className="px-5 py-3 text-sm text-ink/55">{t("common.empty")}</li> : null}
            {[...d.notes].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((n) => (
              <li key={n.id} className="px-5 py-3">
                <p className="text-sm">{n.body}</p>
                <p className="text-xs text-ink/50 mt-0.5">{shortDate(n.createdAt, locale)} · {n.source}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <div className="space-y-4">
        <Card>
          <CardHeader title={t("task.add")} />
          <form
            className="px-5 pb-5 space-y-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (taskTitle.trim()) addTask.mutate();
            }}
          >
            <Input placeholder={t("task.title")} value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} />
            <Input type="date" label={t("task.due")} value={taskDue} onChange={(e) => setTaskDue(e.target.value)} />
            <Button type="submit" className="w-full" loading={addTask.isPending}>{t("task.add")}</Button>
          </form>
          <ul className="divide-y divide-ink/5 border-t border-ink/5">
            {d.tasks.filter((x) => x.status === "open").map((x) => (
              <li key={x.id} className="px-5 py-2 text-sm flex justify-between gap-2">
                <span className="truncate">{x.title}</span>
                <span className="text-ink/50 shrink-0">{relativeDays(daysBetween(x.dueAt), locale)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <CardHeader title={t("customer.contact")} />
          <div className="px-5 pb-5 text-sm space-y-1 text-ink/80">
            {c.address ? <p>{c.address.line1}<br />{c.address.city}, {c.address.state} {c.address.zip}</p> : null}
            {c.dob ? <p>DOB {c.dob}</p> : null}
            <p className="text-ink/55">{t("customer.consents")}:</p>
            <ul className="flex flex-wrap gap-1">
              {d.consents.map((k) => (
                <li key={k.id}><Badge tone={k.revokedAt ? "red" : "green"}>{k.kind.replace(/_/g, " ")}</Badge></li>
              ))}
              {d.consents.length === 0 ? <li className="text-ink/50">—</li> : null}
            </ul>
          </div>
        </Card>
        {d.serviceRequests.length ? (
          <Card>
            <CardHeader title={t("customer.serviceRequests")} />
            <ul className="divide-y divide-ink/5">
              {d.serviceRequests.map((s) => (
                <li key={s.id} className="px-5 py-2 text-sm flex justify-between gap-2">
                  <span>{s.kind.replace(/_/g, " ")}</span>
                  <Badge tone={s.status === "completed" ? "green" : s.status === "rejected" ? "red" : "amber"}>{s.status.replace(/_/g, " ")}</Badge>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function QuotesTab({ d, onChanged }: { d: CustomerDetail; onChanged: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [bridge, setBridge] = useState<BridgeImportResult | null>(null);
  const importBridge = useMutation({
    mutationFn: async (file: File) => {
      const base64 = await fileToBase64(file);
      return callAction<"agency.bridge.import", BridgeImportResult>("agency.bridge.import", { customerId: d.customer.id, filename: file.name, base64 });
    },
    onSuccess: (r) => {
      onChanged();
      setBridge(r.result);
      toast({ title: t("bridge.imported"), body: `${r.result.provenance.format} · ${r.result.provenance.provider}`, tone: "success" });
      if (r.result.quoteRequest) navigate(`/quotes/${r.result.quoteRequest.id}`);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const bridgeCard = (
    <Card className="p-4 md:col-span-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <p className="font-semibold inline-flex items-center gap-2"><FileInput className="h-4 w-4" />{t("bridge.import")}</p>
          <p className="text-sm text-ink/55">{t("bridge.help")}</p>
        </div>
        <label className="inline-flex min-h-tap cursor-pointer items-center gap-2 rounded-xl border border-ink/15 bg-white px-4 text-sm font-semibold hover:bg-ink/5">
          <Upload className="h-4 w-4" />{importBridge.isPending ? t("common.loading") : t("bridge.pick")}
          <input type="file" accept=".xml,.tt2,.tt2x,.txt,text/xml,application/xml" className="sr-only" disabled={importBridge.isPending} onChange={(e) => { const f = e.target.files?.[0]; if (f) importBridge.mutate(f); e.target.value = ""; }} />
        </label>
      </div>
      {bridge && !bridge.quoteRequest ? (
        <div className="mt-3 rounded-2xl bg-surface p-3 text-sm">
          <p className="font-semibold">{t("bridge.noQuote")} v{bridge.application.version} · {t("bridge.format")}: {bridge.provenance.format}</p>
          {bridge.provenance.warnings.length ? <ul className="mt-1 list-disc pl-5 text-ink/70">{bridge.provenance.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul> : null}
        </div>
      ) : null}
    </Card>
  );
  if (d.quotes.length === 0) return <div className="grid gap-3 md:grid-cols-2">{bridgeCard}<div className="md:col-span-2"><EmptyState title={t("customer.noQuotes")} /></div></div>;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {bridgeCard}
      {[...d.quotes].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt)).map((qr) => {
        const best = qr.offers.filter((o) => o.premiumCents != null).sort((a, b) => a.premiumCents! - b.premiumCents!)[0];
        return (
          <Card key={qr.id} className="p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold">{t("quote.version")}{qr.applicationVersion} · {shortDate(qr.requestedAt, locale)}</p>
                <p className="text-xs text-ink/55">{qr.offers.filter((o) => o.status === "returned").length}/{qr.offers.length} {t("quote.returned").toLowerCase()} · {best ? `${t("today.best")} ${money(best.premiumCents)}` : ""}</p>
              </div>
              <ModeBadge mode={qr.executionMode} />
            </div>
            <Link to={`/quotes/${qr.id}`} className="mt-3 inline-flex min-h-[40px] items-center font-semibold text-primary-dark hover:underline">{t("customer.openQuote")} →</Link>
          </Card>
        );
      })}
    </div>
  );
}

function PoliciesTab({ d }: { d: CustomerDetail }) {
  const t = useT();
  const { locale } = useLocale();
  const [sheet, setSheet] = useState<{ open: boolean; policy: Policy | null }>({ open: false, policy: null });
  const addBtn = <Button size="sm" onClick={() => setSheet({ open: true, policy: null })}><Plus className="h-4 w-4" />{t("policies.add")}</Button>;
  return (
    <div className="space-y-3">
      {d.policies.length === 0 ? <EmptyState title={t("customer.noPolicies")} action={addBtn} /> : (
        <>
          <div className="flex justify-end">{addBtn}</div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink/55">
                <tr>
                  <th className="px-4 py-3">{t("common.carrier")}</th>
                  <th className="px-4 py-3">{t("common.line")}</th>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">{t("common.status")}</th>
                  <th className="px-4 py-3">{t("common.expires")}</th>
                  <th className="px-4 py-3">{t("common.premium")}</th>
                  <th className="px-4 py-3"><span className="sr-only">{t("common.actions")}</span></th>
                </tr>
              </thead>
              <tbody>
                {d.policies.map((p) => (
                  <tr key={p.id} className="border-t border-ink/5">
                    <td className="px-4 py-2 font-semibold">{p.carrier}<span className="block text-[11px] font-normal text-ink/45">{t(`policies.source.${p.source}`)}{Object.keys(p.externalIds ?? {}).length ? ` · ${Object.entries(p.externalIds).map(([k, v]) => `${k}:${v}`).join(" ")}` : ""}</span></td>
                    <td className="px-4 py-2">{p.line.replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.policyNumber}</td>
                    <td className="px-4 py-2"><Badge tone={p.status === "active" ? "green" : p.status === "cancelled" || p.status === "expired" ? "neutral" : "amber"}>{t(`policy.status.${p.status}`)}</Badge> {p.remarket ? <Badge tone="amber">{t("renewals.remarket")}</Badge> : null}</td>
                    <td className="px-4 py-2">{shortDate(p.expirationDate, locale)}</td>
                    <td className="px-4 py-2">{money(p.premiumCents)}{p.priorTermPremiumCents != null ? <span className="text-xs text-ink/50"> ({t("common.from")} {money(p.priorTermPremiumCents)})</span> : null}</td>
                    <td className="px-4 py-2 text-right"><Button variant="outline" size="sm" onClick={() => setSheet({ open: true, policy: p })}><Pencil className="h-4 w-4" />{t("customers.edit")}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
      <PolicySheet open={sheet.open} onClose={() => setSheet((s) => ({ ...s, open: false }))} customerId={d.customer.id} policy={sheet.policy} />
      <PolicyHistory customerId={d.customer.id} policies={d.policies} />
    </div>
  );
}

/** Open carrier notices on this customer, with the same cards as the Notices screen. */
function CustomerNotices({ customerId }: { customerId: string }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ["notices", "customer", customerId], queryFn: async () => (await callAction<"agency.notice.list", NoticeRow[]>("agency.notice.list", { status: "all", mine: false, customerId, limit: 50 })).result });
  const rows = q.data ?? [];
  const open = rows.filter((r) => r.notice.status === "new" || r.notice.status === "assigned" || r.notice.status === "contacted");
  if (!rows.length && !adding) return (
    <Card>
      <CardHeader title={t("notices.customerCard")} action={<Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("notices.add")}</Button>} />
      <p className="px-5 pb-4 text-sm text-ink/55">{t("common.empty")}</p>
      <AddNoticeSheet open={adding} onClose={() => setAdding(false)} customerId={customerId} />
    </Card>
  );
  return (
    <Card>
      <CardHeader title={t("notices.customerCard")} action={<div className="flex items-center gap-2"><Badge tone={open.length ? "amber" : "neutral"}>{open.length}</Badge><Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("notices.add")}</Button></div>} />
      <div className="px-4 pb-4"><NoticeList rows={[...open, ...rows.filter((r) => !open.includes(r)).slice(0, 3)]} /></div>
      <AddNoticeSheet open={adding} onClose={() => setAdding(false)} customerId={customerId} />
    </Card>
  );
}

/** The dated ledger of everything that happened to this customer's policies. */
function PolicyHistory({ customerId, policies }: { customerId: string; policies: Policy[] }) {
  const t = useT();
  const { locale } = useLocale();
  const q = useQuery({ queryKey: ["policy-history", customerId], queryFn: async () => (await callAction<"agency.policy.history", Array<PolicyChange & { actorName: string }>>("agency.policy.history", { customerId, limit: 200 })).result });
  if (!policies.length) return null;
  const label = (id: string) => { const p = policies.find((x) => x.id === id); return p ? `${p.carrier} ${p.policyNumber}` : id; };
  return (
    <Card>
      <CardHeader title={t("notices.history")} />
      <ul className="divide-y divide-ink/5">
        {q.data && q.data.length === 0 ? <li className="px-5 py-3 text-sm text-ink/55">{t("notices.history.none")}</li> : null}
        {(q.data ?? []).map((h) => (
          <li key={h.id} className="flex items-start gap-3 px-5 py-2.5 text-sm">
            <span className="mt-0.5 w-24 shrink-0 text-xs text-ink/50">{shortDate(h.at, locale)}</span>
            <div className="min-w-0 flex-1">
              <p><Badge tone={h.kind === "cancelled" ? "red" : h.kind === "carrier_notice" ? "amber" : h.kind === "created" || h.kind === "renewed" || h.kind === "reinstated" ? "green" : "neutral"}>{h.kind.replace(/_/g, " ")}</Badge> <span className="text-ink/80">{h.summary}</span></p>
              <p className="text-xs text-ink/50">{label(h.policyId)} · {h.actorName} · {h.source}</p>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DocumentsTab({ d, latestAppRef, onChanged }: { d: CustomerDetail; latestAppRef: string | null; onChanged: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const prepare = useMutation({
    mutationFn: () => callAction<"agency.form.prepare", DocumentRef>("agency.form.prepare", { customerId: d.customer.id, form: "application_summary", sourceRecord: latestAppRef! }),
    onSuccess: (r) => {
      onChanged();
      toast({ title: t("customer.summaryReady"), tone: "success" });
      window.open(r.result.url, "_blank", "noopener");
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const openDoc = useMutation({
    mutationFn: (documentId: string) => callAction<"agency.document.get", DocumentRef>("agency.document.get", { documentId }),
    onSuccess: (r) => window.open(r.result.url, "_blank", "noopener"),
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const [upTitle, setUpTitle] = useState("");
  const [upKind, setUpKind] = useState<"upload" | "dec_page" | "id_card" | "certificate">("upload");
  const [upFile, setUpFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<DocumentRef | null>(null);
  const upload = useMutation({
    mutationFn: async () => {
      if (!upFile) throw new Error(t("common.file"));
      if (upFile.size > 10 * 1024 * 1024) throw new Error(t("documents.tooLarge"));
      const base64 = await fileToBase64(upFile);
      return callAction<"agency.document.upload", DocumentRef>("agency.document.upload", { customerId: d.customer.id, title: upTitle.trim() || upFile.name, kind: upKind, contentType: upFile.type || "application/octet-stream", base64 });
    },
    onSuccess: (r) => {
      setUploaded(r.result);
      setUpFile(null);
      setUpTitle("");
      onChanged();
      toast({ title: t("documents.uploaded"), body: `${formatBytes(r.result.document.sizeBytes)} · ${r.result.document.checksumSha256.slice(0, 12)}…`, tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  return (
    <div className="space-y-3">
      <Card className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="font-semibold">{t("customer.prepareSummary")}</p>
          <p className="text-sm text-ink/55">{latestAppRef ?? t("customer.needsApplication")}</p>
        </div>
        <Button onClick={() => prepare.mutate()} disabled={!latestAppRef} loading={prepare.isPending}><FileText className="h-4 w-4" />{t("customer.prepareSummary")}</Button>
      </Card>
      <Card className="p-4">
        <p className="font-semibold inline-flex items-center gap-2 mb-2"><Upload className="h-4 w-4" />{t("documents.upload")}</p>
        <form
          className="grid gap-3 sm:grid-cols-[1fr_180px_auto_auto] sm:items-end"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (upFile) upload.mutate();
          }}
        >
          <Input label={t("documents.titleField")} value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder={upFile?.name ?? ""} />
          <Select label={t("common.kind")} value={upKind} onChange={(e) => setUpKind(e.target.value as typeof upKind)}>
            {(["upload", "dec_page", "id_card", "certificate"] as const).map((k) => <option key={k} value={k}>{t(`documents.kind.${k}`)}</option>)}
          </Select>
          <label className="inline-flex min-h-tap cursor-pointer items-center gap-2 rounded-xl border border-ink/15 bg-white px-4 text-sm font-semibold hover:bg-ink/5">
            {upFile ? `${upFile.name} (${formatBytes(upFile.size)})` : t("documents.pick")}
            <input type="file" className="sr-only" onChange={(e) => setUpFile(e.target.files?.[0] ?? null)} />
          </label>
          <Button type="submit" disabled={!upFile} loading={upload.isPending}>{upload.isPending ? t("documents.uploading") : t("documents.upload")}</Button>
        </form>
        {uploaded ? <p className="mt-2 text-xs text-ink/55">{t("documents.checksum")}: <span className="font-mono">{uploaded.document.checksumSha256}</span> · {formatBytes(uploaded.document.sizeBytes)}</p> : null}
      </Card>
      {d.documents.length === 0 ? <EmptyState title={t("customer.noDocs")} /> : null}
      {d.documents.length ? (
        <Card>
          <ul className="divide-y divide-ink/5">
            {d.documents.map((doc) => (
              <li key={doc.id} className="flex items-center gap-3 px-5 py-3">
                <FileText className="h-5 w-5 text-ink/40" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{doc.title}</p>
                  <p className="text-xs text-ink/55">{doc.formEdition ?? doc.kind.replace(/_/g, " ")} · {shortDate(doc.createdAt, locale)} · {doc.sourceRecord ?? ""}</p>
                </div>
                <ModeBadge mode={doc.executionMode} />
                <Button variant="outline" size="sm" onClick={() => openDoc.mutate(doc.id)}>{t("common.open")}</Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

const CONSENT_KINDS = ["sms_transactional", "sms_marketing", "email_marketing", "e_delivery", "credit_disclosure", "privacy_notice"] as const;
const CONSENT_SOURCES = ["in_person", "phone", "web_form", "sms_reply", "in_app"] as const;

function ConsentsTab({ d, onChanged }: { d: CustomerDetail; onChanged: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const c = d.customer;
  const [kind, setKind] = useState<(typeof CONSENT_KINDS)[number]>("sms_transactional");
  const [source, setSource] = useState<(typeof CONSENT_SOURCES)[number]>("in_person");
  const [cLocale, setCLocale] = useState<"en" | "es">(c.preferredLocale === "es" ? "es" : "en");
  const [revoking, setRevoking] = useState<Consent | null>(null);
  const channelValue = kind.startsWith("sms") ? c.phones[0]?.number : kind === "email_marketing" ? c.email : undefined;
  const consentText = (k: string, l: "en" | "es") => (l === "es" ? esText(k) : t(`consents.text.${k}`));
  // The exact text shown is versioned so the record can be reproduced later.
  const formTextVersion = `${kind}-${cLocale}-2026-10`;

  const capture = useMutation({
    mutationFn: () => callAction<"agency.consent.capture", Consent>("agency.consent.capture", { customerId: c.id, kind, channelValue, source, formTextVersion, locale: cLocale }),
    onSuccess: () => {
      onChanged();
      toast({ title: t("consents.capturedToast"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const revoke = useMutation({
    mutationFn: (consentId: string) => callAction<"agency.consent.revoke", Consent>("agency.consent.revoke", { consentId }),
    onSuccess: () => {
      onChanged();
      toast({ title: t("consents.revokedToast"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  return (
    <div className="grid gap-4 md:grid-cols-5">
      <Card className="md:col-span-3">
        <CardHeader title={t("consents.title")} />
        {d.consents.length === 0 ? <p className="px-5 pb-5 text-sm text-ink/55">{t("consents.none")}</p> : null}
        <ul className="divide-y divide-ink/5">
          {[...d.consents].sort((a, b) => b.capturedAt.localeCompare(a.capturedAt)).map((k) => (
            <li key={k.id} className="px-5 py-3 flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold">{t(`consents.kind.${k.kind}`)} {k.channelValue ? <span className="text-ink/55 font-normal">· {k.channelValue.includes("@") ? k.channelValue : formatPhone(k.channelValue)}</span> : null}</p>
                <p className="text-xs text-ink/55">{t(`consents.source.${k.source}`) === `consents.source.${k.source}` ? k.source : t(`consents.source.${k.source}`)} · {t("consents.form")} {k.formTextVersion} · {k.locale.toUpperCase()} · {t("consents.captured")} {shortDate(k.capturedAt, locale)}{k.revokedAt ? ` · ${t("consents.revoked")} ${shortDate(k.revokedAt, locale)}` : ""}</p>
              </div>
              <Badge tone={k.revokedAt ? "red" : "green"}>{k.revokedAt ? t("consents.revoked") : t("consents.active")}</Badge>
              {!k.revokedAt ? <Button variant="outline" size="sm" onClick={() => setRevoking(k)}>{t("consents.revoke")}</Button> : null}
            </li>
          ))}
        </ul>
      </Card>
      <Card className="md:col-span-2">
        <CardHeader title={t("consents.capture")} />
        <form
          className="px-5 pb-5 space-y-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            capture.mutate();
          }}
        >
          <Select label={t("consents.kind")} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            {CONSENT_KINDS.map((k) => <option key={k} value={k}>{t(`consents.kind.${k}`)}</option>)}
          </Select>
          <Select label={t("consents.source")} value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
            {CONSENT_SOURCES.map((s) => <option key={s} value={s}>{t(`consents.source.${s}`)}</option>)}
          </Select>
          <Select label={t("common.language")} value={cLocale} onChange={(e) => setCLocale(e.target.value as "en" | "es")}>
            <option value="en">{t("common.english")}</option>
            <option value="es">{t("common.spanish")}</option>
          </Select>
          {channelValue ? <p className="text-xs text-ink/55">{t("consents.channelValue")}: <strong>{channelValue.includes("@") ? channelValue : formatPhone(channelValue)}</strong></p> : null}
          <div className="rounded-2xl bg-surface p-3 text-sm text-ink/80">
            <p className="text-xs font-semibold text-ink/55 mb-1">{t("consents.text")} · {formTextVersion}</p>
            <p>{consentText(kind, cLocale)}</p>
          </div>
          <Button type="submit" className="w-full" loading={capture.isPending}>{t("consents.capture")}</Button>
        </form>
      </Card>
      <ConfirmDialog
        open={!!revoking}
        title={t("consents.revoke.title")}
        body={t("consents.revoke.body")}
        confirmLabel={t("consents.revoke.confirm")}
        keepLabel={t("consents.revoke.keep")}
        danger
        onKeep={() => setRevoking(null)}
        onConfirm={() => {
          const id = revoking?.id;
          setRevoking(null);
          if (id) revoke.mutate(id);
        }}
      />
    </div>
  );

  function esText(k: string) {
    // Spanish consent text comes from the es dictionary regardless of the UI language.
    return translate("es", `consents.text.${k}`);
  }
}

function ActivityTab({ d }: { d: CustomerDetail }) {
  const t = useT();
  const { locale } = useLocale();
  if (d.timeline.length === 0) return <EmptyState title={t("common.empty")} />;
  return (
    <Card>
      <ol className="divide-y divide-ink/5">
        {d.timeline.map((it) => (
          <li key={it.id} className="px-5 py-3 flex gap-3">
            <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-sm">{it.title} {it.aiInvolved ? <Badge tone="blue">AI</Badge> : null}</p>
              {it.body ? <p className="text-sm text-ink/70">{it.body}</p> : null}
              <p className="text-xs text-ink/50">{shortDate(it.at, locale)} · {it.kind}{it.actor ? ` · ${it.actor}` : ""}{it.source ? ` · ${it.source}` : ""}</p>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
